'use client';

import { mergeProps } from '@base-ui/react/merge-props';
import { Tooltip as TooltipPrimitive } from '@base-ui/react/tooltip';
import * as React from 'react';

import {
  usePortalContainerOverride,
  usePortalReplacementRef,
} from '@/components/ui/portal-container';
import { cn } from '@/lib/utils';

function TooltipProvider({
  delay = 0,
  ...props
}: TooltipPrimitive.Provider.Props) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delay={delay}
      {...props}
    />
  );
}

interface TooltipA11yState {
  id: string;
  open: boolean;
  hidden: boolean;
  ownerDocumentBody: HTMLElement | null | undefined;
  registerTrigger: React.RefCallback<HTMLElement>;
  setPopup: (id: string | undefined, hidden?: boolean) => void;
}

const TooltipA11yContext = React.createContext<TooltipA11yState | null>(null);

/**
 * Base UI 1.8 sets neither `role="tooltip"` nor `aria-describedby`. This Root
 * tracks open state (controlled `open` wins) and shares the popup id so the
 * trigger references the popup only while it is open.
 */
function TooltipA11yRoot({
  open: openProp,
  defaultOpen = false,
  onOpenChange,
  disabled,
  triggerId: triggerIdProp,
  defaultTriggerId,
  ...props
}: TooltipPrimitive.Root.Props) {
  const generatedId = React.useId();
  const [customId, setCustomId] = React.useState<string>();
  const [popupHidden, setPopupHidden] = React.useState(false);
  const [triggers, setTriggers] = React.useState<HTMLElement[]>([]);
  // undefined: no opening event yet; null: an event without a trigger element.
  const [eventTrigger, setEventTrigger] = React.useState<Element | null>();
  const explicitTriggerId = triggerIdProp ?? defaultTriggerId;
  const [ownerDocument, setOwnerDocument] = React.useState<{
    triggerId: typeof explicitTriggerId;
    eventTrigger: typeof eventTrigger;
    body: HTMLElement | null | undefined;
  } | null>(null);
  const registerTrigger = React.useCallback((node: HTMLElement | null) => {
    if (!node) return;
    setTriggers((current) => [...current, node]);
    return () => {
      setTriggers((current) => current.filter((t) => t !== node));
    };
  }, []);
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen);
  const open = (openProp ?? uncontrolledOpen) && !disabled;
  const previousTriggers = React.useRef(triggers);
  React.useLayoutEffect(() => {
    // Ref replacement and StrictMode may unregister and reattach in one commit.
    // Clear implicit ownership only after a registered trigger actually leaves.
    if (
      previousTriggers.current.some((node) => node === eventTrigger) &&
      !triggers.some((node) => node === eventTrigger)
    ) {
      setEventTrigger(undefined);
    }
    previousTriggers.current = triggers;
  }, [eventTrigger, triggers]);
  React.useEffect(() => {
    const trigger =
      triggerIdProp != null && eventTrigger?.id !== triggerIdProp
        ? triggers.find((node) => node.id === triggerIdProp)
        : eventTrigger !== undefined
          ? eventTrigger
          : explicitTriggerId != null
            ? triggers.find((node) => node.id === explicitTriggerId)
            : triggers[0];
    setOwnerDocument({
      triggerId: explicitTriggerId,
      eventTrigger,
      // Defer eventless opens until local triggers mount. Detached handles and
      // unresolvable opening events retain Base UI's default portal fallback.
      body:
        trigger?.ownerDocument.body ??
        (props.handle || eventTrigger === null ? undefined : null),
    });
  }, [explicitTriggerId, triggerIdProp, eventTrigger, triggers, props.handle]);
  // Never reuse another trigger's document while its replacement resolves.
  const ownerDocumentBody =
    ownerDocument &&
    ownerDocument.triggerId === explicitTriggerId &&
    ownerDocument.eventTrigger === eventTrigger
      ? ownerDocument.body
      : null;
  const setPopup = React.useCallback(
    (id: string | undefined, hidden = false) => {
      setCustomId(id);
      setPopupHidden(hidden);
    },
    [],
  );
  const state = React.useMemo(
    () => ({
      id: customId ?? generatedId,
      open,
      hidden: popupHidden,
      ownerDocumentBody,
      registerTrigger,
      setPopup,
    }),
    [
      customId,
      generatedId,
      open,
      popupHidden,
      ownerDocumentBody,
      registerTrigger,
      setPopup,
    ],
  );
  return (
    <TooltipA11yContext.Provider value={state}>
      <TooltipPrimitive.Root
        open={openProp}
        defaultOpen={defaultOpen}
        disabled={disabled}
        triggerId={triggerIdProp}
        defaultTriggerId={defaultTriggerId}
        onOpenChange={(nextOpen, details) => {
          onOpenChange?.(nextOpen, details);
          if (details.isCanceled) return;
          setUncontrolledOpen(nextOpen);
          if (nextOpen) {
            const target = details.event.target;
            setEventTrigger(
              details.trigger ??
                (target && 'nodeType' in target && target.nodeType === 1
                  ? (target as Element)
                  : null),
            );
          }
        }}
        {...props}
      />
    </TooltipA11yContext.Provider>
  );
}

function mergeDescribedBy(...ids: Array<string | undefined>) {
  const merged = new Set(ids.flatMap((v) => v?.split(/\s+/) ?? []));
  merged.delete('');
  return merged.size ? [...merged].join(' ') : undefined;
}

function mergeRefs<T>(
  ...refs: Array<React.Ref<T> | undefined>
): React.RefCallback<T> {
  return (node) => {
    const cleanups: Array<() => void> = [];
    for (const ref of refs) {
      if (typeof ref === 'function') {
        const cleanup = ref(node);
        cleanups.push(() => {
          if (cleanup) cleanup();
          else ref(null);
        });
      } else if (ref) {
        (ref as React.RefObject<T | null>).current = node;
        cleanups.push(() => {
          (ref as React.RefObject<T | null>).current = null;
        });
      }
    }
    return () => {
      for (const cleanup of cleanups) cleanup();
    };
  };
}

/**
 * Trigger props: appends the popup id to `aria-describedby` while open. Base UI
 * lets a `render` element's own props win over injected ones, so an element
 * carrying its own `aria-describedby` is re-rendered with the merged value
 * (its own ref is merged with Base UI's).
 *
 * Limitation: detached triggers (Base UI `handle`, outside Root) and `render`
 * callbacks that set their own `aria-describedby` after spreading props are
 * not wired.
 */
function useTooltipTriggerA11y(
  {
    'aria-describedby': describedBy,
    render,
  }: Pick<TooltipPrimitive.Trigger.Props, 'aria-describedby' | 'render'>,
  forwardedRef?: React.Ref<HTMLElement>,
): Pick<TooltipPrimitive.Trigger.Props, 'aria-describedby' | 'render'> & {
  ref: React.RefCallback<HTMLElement>;
} {
  const ctx = React.useContext(TooltipA11yContext);
  const registerTrigger = ctx?.registerTrigger;
  const ref = React.useMemo(
    () => mergeRefs(registerTrigger, forwardedRef),
    [registerTrigger, forwardedRef],
  );
  const elementProps = React.isValidElement<Record<string, unknown>>(render)
    ? render.props
    : undefined;
  const merged = mergeDescribedBy(
    describedBy,
    elementProps?.['aria-describedby'] as string | undefined,
    ctx?.open && !ctx.hidden ? ctx.id : undefined,
  );
  const ownsDescribedBy = !!elementProps?.['aria-describedby'];
  const renderRefs = React.useRef<{
    base?: React.Ref<HTMLElement>;
    consumer?: React.Ref<HTMLElement>;
    merged: React.RefCallback<HTMLElement>;
  } | null>(null);
  return {
    ref,
    'aria-describedby': merged,
    render:
      ownsDescribedBy && React.isValidElement(render)
        ? (props: React.HTMLAttributes<HTMLElement>) => {
            const base = (props as { ref?: React.Ref<HTMLElement> }).ref;
            const consumer = elementProps?.ref as
              | React.Ref<HTMLElement>
              | undefined;
            // Open/context changes update descriptions, not ref registration.
            if (
              !renderRefs.current ||
              renderRefs.current.base !== base ||
              renderRefs.current.consumer !== consumer
            ) {
              renderRefs.current = {
                base,
                consumer,
                merged: mergeRefs(base, consumer),
              };
            }
            return React.createElement(render.type, {
              ...mergeProps(props, render.props as typeof props),
              ref: renderRefs.current.merged,
              'aria-describedby': merged,
            });
          }
        : render,
  };
}

/** Popup props: `role="tooltip"` + id shared with the trigger. */
function useTooltipPopupA11y(id: string | undefined, hidden?: boolean) {
  const ctx = React.useContext(TooltipA11yContext);
  const setPopup = ctx?.setPopup;
  React.useEffect(() => {
    setPopup?.(id, !!hidden);
    return () => setPopup?.(undefined);
  }, [hidden, id, setPopup]);
  return {
    role: 'tooltip' as const,
    id: id ?? ctx?.id,
    ownerDocumentBody: ctx ? ctx.ownerDocumentBody : undefined,
  };
}

function Tooltip({ ...props }: TooltipPrimitive.Root.Props) {
  return (
    <TooltipProvider>
      <TooltipA11yRoot data-slot="tooltip" {...props} />
    </TooltipProvider>
  );
}

const TooltipTrigger = React.forwardRef<
  HTMLElement,
  TooltipPrimitive.Trigger.Props
>(function TooltipTrigger(
  { 'aria-describedby': describedBy, render, ...props },
  ref,
) {
  return (
    <TooltipPrimitive.Trigger
      data-slot="tooltip-trigger"
      {...props}
      {...useTooltipTriggerA11y(
        { 'aria-describedby': describedBy, render },
        ref,
      )}
    />
  );
});

function TooltipPopup({
  className,
  side = 'top',
  sideOffset = 4,
  align = 'center',
  alignOffset = 0,
  children,
  container,
  ...props
}: TooltipPrimitive.Popup.Props &
  Pick<
    TooltipPrimitive.Positioner.Props,
    'align' | 'alignOffset' | 'side' | 'sideOffset'
  > & {
    /**
     * DOM node to portal the popup into. Falls back to
     * `usePortalContainerOverride()` (which checks `PortalContainerProvider`), then to
     * the active trigger owner document body. Eventless opens defer until
     * registered triggers mount; unresolvable events use Base UI's default.
     */
    container?: HTMLElement | null;
  }) {
  const resolvedContainer = usePortalContainerOverride(container);
  const { ownerDocumentBody, ...a11y } = useTooltipPopupA11y(
    props.id,
    props.hidden,
  );
  const portalContainer =
    resolvedContainer !== undefined ? resolvedContainer : ownerDocumentBody;
  const { ref: popupRef } = usePortalReplacementRef(portalContainer, props.ref);
  return (
    <TooltipPrimitive.Portal container={portalContainer}>
      <TooltipPrimitive.Positioner
        align={align}
        alignOffset={alignOffset}
        side={side}
        sideOffset={sideOffset}
        className="isolate z-50"
      >
        <TooltipPrimitive.Popup
          data-slot="tooltip-popup"
          className={cn(
            'motion-safe:transition-opacity motion-safe:duration-(--duration-fast) motion-safe:ease-standard motion-safe:data-starting-style:opacity-0 motion-safe:data-ending-style:opacity-0 data-[instant=delay]:transition-none data-[instant=delay]:duration-0 data-[instant=delay]:data-starting-style:opacity-100 data-[instant=delay]:data-ending-style:opacity-100 rounded-md px-3 py-1.5 text-xs bg-foreground text-background z-50 w-fit max-w-xs origin-(--transform-origin)',
            className,
          )}
          {...props}
          {...a11y}
          ref={popupRef}
        >
          {children}
          <TooltipPrimitive.Arrow className="w-3 h-2 bg-foreground data-[side=top]:-bottom-2 data-[side=top]:[clip-path:polygon(50%_100%,0_0,100%_0)] data-[side=bottom]:-top-2 data-[side=bottom]:[clip-path:polygon(50%_0,0_100%,100%_100%)] data-[side=left]:-right-2 data-[side=left]:w-2 data-[side=left]:h-3 data-[side=left]:[clip-path:polygon(100%_50%,0_0,0_100%)] data-[side=right]:-left-2 data-[side=right]:w-2 data-[side=right]:h-3 data-[side=right]:[clip-path:polygon(0_50%,100%_0,100%_100%)]" />
        </TooltipPrimitive.Popup>
      </TooltipPrimitive.Positioner>
    </TooltipPrimitive.Portal>
  );
}

TooltipProvider.displayName = 'Tooltip.Provider';
Tooltip.displayName = 'Tooltip';
TooltipTrigger.displayName = 'Tooltip.Trigger';
TooltipPopup.displayName = 'Tooltip.Popup';

export {
  Tooltip,
  TooltipA11yRoot,
  TooltipPopup,
  TooltipProvider,
  TooltipTrigger,
  useTooltipPopupA11y,
  useTooltipTriggerA11y,
};
